import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

// ============================================================================
// PERFORMANCE TRACKING: Track app launch performance
// ============================================================================
const appStartTime = performance.now();

// Mark navigation start for accurate TTI calculation
if (performance.mark) {
  performance.mark('app-bootstrap-start');
}

if (environment.production) {
  enableProdMode();
}

export function getBaseUrl() {
  return document.getElementsByTagName('base')[0].href;
}

const providers = [
  { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] }
];

// Helper functions for performance metrics
function getFirstContentfulPaint(): number | null {
  const perfEntries = performance.getEntriesByType('paint');
  const fcpEntry = perfEntries.find(entry => entry.name === 'first-contentful-paint');
  return fcpEntry ? Math.round(fcpEntry.startTime) : null;
}

function getLargestContentfulPaint(): number | null {
  return new Promise((resolve) => {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as any;
      resolve(Math.round(lastEntry.renderTime || lastEntry.loadTime));
      observer.disconnect();
    });
    
    try {
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      // Timeout after 10 seconds
      setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, 10000);
    } catch (e) {
      resolve(null);
    }
  }) as any;
}

function getLargestResourceTiming(): { name: string; duration: number; size: number } | null {
  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  if (resources.length === 0) return null;
  
  const largest = resources.reduce((prev, current) => 
    current.duration > prev.duration ? current : prev
  );
  
  return {
    name: largest.name.split('/').pop() || 'unknown',
    duration: Math.round(largest.duration),
    size: Math.round((largest.transferSize || 0) / 1024) // KB
  };
}

function getConnectionType(): string {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  return connection ? connection.effectiveType || 'unknown' : 'unknown';
}

function getConnectionDetails(): any {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  if (!connection) return null;
  
  return {
    effective_type: connection.effectiveType, // '4g', '3g', '2g', 'slow-2g'
    downlink_mbps: connection.downlink, // Mbps
    rtt_ms: connection.rtt, // Round trip time in ms
    save_data: connection.saveData // User has data saver enabled
  };
}

function getMemoryUsage(): any {
  if ((performance as any).memory) {
    return {
      used_js_heap_mb: Math.round((performance as any).memory.usedJSHeapSize / 1048576),
      total_js_heap_mb: Math.round((performance as any).memory.totalJSHeapSize / 1048576),
      limit_js_heap_mb: Math.round((performance as any).memory.jsHeapSizeLimit / 1048576)
    };
  }
  return null;
}

platformBrowserDynamic(providers).bootstrapModule(AppModule)
  .then(async () => {
    // App successfully bootstrapped
    const bootstrapTime = performance.now() - appStartTime;
    
    if (performance.mark) {
      performance.mark('app-bootstrap-end');
      performance.measure('app-bootstrap', 'app-bootstrap-start', 'app-bootstrap-end');
    }

    // Get navigation timing
    const navTiming = performance.timing;
    const domContentLoaded = navTiming.domContentLoadedEventEnd - navTiming.navigationStart;
    const pageLoadComplete = navTiming.loadEventEnd - navTiming.navigationStart;

    // Wait for LCP measurement (async)
    const lcp = await getLargestContentfulPaint();

    // Prepare performance data
    const performanceData = {
      // Bootstrap timing
      bootstrap_time_ms: Math.round(bootstrapTime),
      is_slow_bootstrap: bootstrapTime > 3000,
      
      // Web Vitals
      first_contentful_paint_ms: getFirstContentfulPaint(),
      largest_contentful_paint_ms: lcp,
      dom_content_loaded_ms: domContentLoaded,
      page_load_complete_ms: pageLoadComplete,
      
      // Resource timing
      largest_resource: getLargestResourceTiming(),
      
      // Network
      connection_type: getConnectionType(),
      connection_details: getConnectionDetails(),
      
      // Device
      user_agent: navigator.userAgent,
      platform: navigator.platform,
      hardware_concurrency: navigator.hardwareConcurrency || 'unknown',
      device_memory_gb: (navigator as any).deviceMemory || 'unknown',
      
      // Memory (if available)
      memory_usage: getMemoryUsage(),
      
      // Environment
      is_production: environment.production,
      timestamp: new Date().toISOString()
    };

    console.log('[Performance] App Bootstrap Complete:', performanceData);

    // Store performance data for PostHog to capture later (when service is initialized)
    (window as any).__performanceData = {
      appLaunch: {
        data: performanceData,
        bootstrapTime: bootstrapTime,
        lcp: lcp
      }
    };

    // Track slow assets after page load
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        const slowAssets = perfData
          .filter(r => r.duration > 1000)
          .map(r => ({ 
            name: r.name.split('/').pop() || 'unknown', 
            duration: Math.round(r.duration),
            size_kb: Math.round((r.transferSize || 0) / 1024)
          }))
          .sort((a, b) => b.duration - a.duration)
          .slice(0, 10); // Top 10 slowest
        
        // Store slow assets data for PostHog to capture later
        if (slowAssets.length > 0) {
          if (!(window as any).__performanceData) {
            (window as any).__performanceData = {};
          }
          (window as any).__performanceData.slowAssets = {
            slow_assets: slowAssets,
            total_assets: perfData.length,
            slowest_asset_duration_ms: slowAssets[0].duration
          };
        }
      }, 500);
    }, { once: true });
  })
  .catch(err => {
    console.error('App bootstrap failed:', err);
    
    // Store bootstrap error for PostHog to capture later
    (window as any).__performanceData = {
      bootstrapError: {
        error_message: err.message,
        error_stack: err.stack,
        error_name: err.name,
        time_to_error_ms: Math.round(performance.now() - appStartTime)
      }
    };
    
  });
